"use client"
import { Trailer } from '@filmweb/sanity-types';
import ButtonLink from 'src/components/buttonlink/ButtonLink';
import withWatchlistState from 'src/components/filmwebid/withWatchlistState';
import FilmwebLink from "src/components/filmweblink/FilmwebLink";
import FullPageOverlay from 'src/components/fullpageoverlay/FullPageOverlay';
import SuspenseFallback from 'src/components/suspensefallback/SuspenseFallback';
import theme from 'src/components/themes/DefaultTheme';
import RelatedClip from 'src/components/traileroverlaywrapper/RelatedClip';
import WatchlistButton from 'src/components/traileroverlaywrapper/WatchlistButton';
import { gql } from "graphql-request";
import { useFilminfo } from 'src/lib/client/useFilminfo';
import styled from 'src/lib/styles/css';
import IFilmklipp from "src/lib/types/Filmklipp";
import { toNumber } from 'lodash';
import { useState } from 'react';


//#region [Props]
type TrailerOverlayWrapperProps = {
	video?: Trailer | IFilmklipp;
	videoId?: number;
	onClose: () => void;
	hideRelated?: boolean;
	onSelectTrailer?: (newTrailerId: number) => void;
};
//#endregion

//#region [Component]
export default function TrailerOverlayWrapper({ video, onClose, videoId, hideRelated = false, onSelectTrailer }: TrailerOverlayWrapperProps) {
	const [_videoId, setVideoId] = useState<number | undefined>(() => {
		if (video?.videoId) {
			return toNumber(video.videoId);
		}
		return videoId;
	});
	const filminfoFesult = useFilminfo(MOVIE_CLIP_QUERY, { clipId: _videoId }, { active: !!_videoId });

	if (!_videoId) return null;

	const movieClip = filminfoFesult?.fiData?.movieQuery?.getMovieClip;

	return <FullPageOverlay onClose={onClose}>
		{filminfoFesult.fiLoading && <SuspenseFallback text='Henter trailer' />}
		{!filminfoFesult.fiLoading && movieClip && <SPlayerContainer>
			{movieClip.movieTitle && <SMovieTitle><STitleLink to={`/film/${movieClip.movieId}`}>{movieClip.movieTitle}</STitleLink></SMovieTitle>}
			<SClipTitle><STitleLink to={`/film/${movieClip.movieId}`}>{movieClip.clipTitle}</STitleLink></SClipTitle>
			<SPlayer dangerouslySetInnerHTML={{ __html: movieClip.embedCode! }}></SPlayer>
			<SOther>
				{!hideRelated && (movieClip.relatedClips?.length ?? 0) > 0 && <SRelatedClips>
					{movieClip.relatedClips!.slice(0, 4).map(rc => <RelatedClip key={rc?.id} clip={rc!} onClick={() => { setVideoId(rc!.id); onSelectTrailer?.(rc!.id); }} />)}
				</SRelatedClips>}
			</SOther>
			<SActions>
				{movieClip?.movieId && <SButtonPadder>
					<ButtonLink href={`/film/${movieClip.movieId}`}>Mer om filmen</ButtonLink>
					<WButton edi={movieClip.movieId} />
				</SButtonPadder>}
			</SActions>
		</SPlayerContainer>}
	</FullPageOverlay>;
}
//#endregion


//#region [Component]
const WButton = withWatchlistState(WatchlistButton);
//#endregion

//#region [Styles]
const SPlayerContainer = styled.article`
	display: flex;
	flex-direction: column;

	@media (min-width: 550px) {
        /*@media  (orientation: landscape) {*/
        position: relative;
        display: grid;
        max-height: 1048px;
        height: 100vh;
        height: 100dvh;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        grid-template-rows: auto minmax(0, ${900 / 1048 * 100}%) auto auto;
		grid-template-areas:
			"filmTitle filmTitle"
			"player player"
			"clipTitle other"
			"actions other";
		grid-column-gap: 10px;
		column-gap: 10px;
		grid-row-gap: 10px;
		row-gap: 10px;
	}
`;

const STitleLink = styled(FilmwebLink)`
	color: currentColor;
	text-decoration: none;
`;

const SMovieTitle = styled.h2`
	grid-area: filmTitle;
	display: none;
	font-size: 24px;

	@media (min-width: 550px) {
        /*@media (orientation: landscape) {*/
        display: block;
        max-width: calc(100% - 60px); /*To make absolutely sure it does not overlap the close button*/
    }
`;

const SClipTitle = styled.h1`
	grid-area: clipTitle;
	font-size: 20px;
	padding: 20px 10px;

	@media (min-width: 550px) {
		display: block;
		font-size: 24px;
		padding: 0;
	}
`;

const SPlayer = styled.div`
    grid-area: player;
    max-height: 900px; /*this is the max height the player can be if the screen is 1600px or wider*/

    .video {
        margin: 0 auto;
        @media (max-width: 1620px) {
            width: ${9 / 16 * 100.0}%;
		}
		height: 100%;
	}

`;

const SOther = styled.div`
	grid-area: other;
	overflow-x: auto;
	margin-left: 10px;

	@media (min-width: 550px) {
        /*@media (orientation: landscape) {*/
        overflow: hidden;
        margin-left: 0;
    }
`;

const SRelatedClips = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    column-gap: 10px; /*if not supported, relatedClip will handle it :)*/
    justify-content: flex-start;
    @media (min-width: 550px) {
        /*@media (orientation: landscape) {*/
        justify-content: flex-end;
        column-gap: 30px; /*if not supported, relatedClip will handle it :)*/
    }
`;

const SActions = styled.div`
	grid-area: actions;

	display: flex;
	justify-content: space-around;
	@media (min-width: 550px) {
        /*@media (orientation: landscape) {*/
        justify-content: flex-start;
    }
`;

const SButtonPadder = styled.div`
	padding-bottom: 10px;
`;
//#endregion


//#region [Other]

const MOVIE_CLIP_QUERY = gql`
query ($clipId: Int, $playerId: Int) {
	movieQuery {
		getMovieClip(clipId: $clipId, playerId: $playerId) {
			__typename
			clipTitle
			movieId
			movieTitle
			id
			embedCode
            sanityImageWideUrl
			relatedClips {
                __typename
				id
				clipTitle
                sanityImageWideUrl
			}
		}
	}
}
`;
//#endregion